import React, { useContext, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from './Components/redux/store';
import 'react-toastify/dist/ReactToastify.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import AuthContext, { AuthContextProvider } from './Components/Common/authContext';
import { decryptAES128CTR } from './Components/Common/Decrypt';
import { HelmetProvider } from 'react-helmet-async';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif', gap: '1em' }}>
          <p style={{ color: '#671929', fontWeight: 600, fontSize: '1.1rem' }}>Something went wrong. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '0.6em 1.4em', border: '1px solid #671929', borderRadius: '2em', background: '#671929', color: '#fff', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Stripe key is fetched asynchronously. Elements accepts stripe={null} initially
// (the documented Stripe SSR pattern) and silently upgrades to the real instance
// when the key arrives — no "cannot change stripe prop" error.
// PayPal is not gated here; the checkout route owns its own PayPalScriptProvider.
const StripeProvider = () => {
  const authCtx = useContext(AuthContext);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);

  useEffect(() => {
    const fetchKey = async () => {
      const enc = authCtx.societeConfig?.find(
        item => item.config_module_nomtech === 'stripe_pay_api_key'
      )?.valeur;
      if (enc) {
        const key = await decryptAES128CTR(enc);
        setStripePublishableKey(key);
      }
    };
    fetchKey();
  }, [authCtx.societeConfig]);

  const stripePromise = useMemo(
    () => stripePublishableKey ? loadStripe(stripePublishableKey) : null,
    [stripePublishableKey]
  );

  // No loading gate — the app renders immediately.
  // useStripe() returns null until stripePromise resolves, which is safe.
  return (
    <Elements stripe={stripePromise}>
      <App />
    </Elements>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    <HelmetProvider>
      <Provider store={store}>
        <AuthContextProvider>
          <BrowserRouter>
            <PersistGate loading={null} persistor={persistor}>
              <StripeProvider />
            </PersistGate>
          </BrowserRouter>
        </AuthContextProvider>
      </Provider>
    </HelmetProvider>
  </ErrorBoundary>
);
